import React, { useEffect, useRef, useState } from 'react';
import createIcon from '../../../../assets/createWB.svg';
import crossIcon from '../../../../assets/cross.svg';
import runIcon from '../../../../assets/run.svg';
import { useIntentSignal } from '../../../../hooks/useIntentSignal';
import IntentSignalSlide from './BodySlides/IntentSignalSlide';
import KeywordSlide from './BodySlides/KeywordSlide';
import RulesetSlide from './BodySlides/RulesetSlide';
import BufferingLoader from './BufferingLoader';
import ProgressBar from './ProgressBar';
import { useSidebar } from '../../../../context/SidebarContext';
import { toast, Slide } from 'react-toastify';

interface CreateIntentPopupProps {
  isOpen: boolean;
  onClose: () => void;
}

const CreateIntentPopup: React.FC<CreateIntentPopupProps> = ({ isOpen, onClose }) => {
  const [inputValue, setInputValue] = useState('');
  const [isInitialLoading, setIsInitialLoading] = useState(false);
  const [isKeywordLoading, setIsKeywordLoading] = useState(false);
  const [isSearchComplete, setIsSearchComplete] = useState(false);
  const [signalId, setSignalId] = useState<string | undefined>(undefined);
  const [currentStep, setCurrentStep] = useState(0);
  const [keywords, setKeywords] = useState<string[]>([]);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [nextStep, setNextStep] = useState<number | null>(null);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [rulesets, setRulesets] = useState<string[]>([]);
  const popupRef = useRef<HTMLDivElement>(null);
  const { isSidebarOpen } = useSidebar();

  const { sendIntentSignal, signalDetails, updateIntentSignal } = useIntentSignal(20, signalId);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [onClose]);

  const handleSuggestionSelect = (suggestion: string) => {
    setInputValue(suggestion);
  };

  const handleAddKeyword = (keyword: string) => {
    setKeywords([...keywords, keyword]);
  };

  const handleRemoveKeyword = (index: number) => {
    setKeywords(keywords.filter((_, i) => i !== index));
  };

  const CustomSuccessToast = () => (
    <div className="flex items-start gap-4">
      <div className="flex-1">
        <h3 className="text-[14px] font-semibold text-[#222222] mb-1">
          Intent Signal Created
        </h3>
        <p className="text-[12px] text-[#666666] leading-relaxed">
          Sit back and relax, our agents are looking out for your leads.
        </p>
      </div>
    </div>
  );

  const handleRunClick = async () => {
    if (!inputValue.trim()) return;
    
    setIsTransitioning(true);
    setIsInitialLoading(true);
    
    try {
      const response = await sendIntentSignal(inputValue);
      setSignalId(response._id);
      if (response.keywords && Array.isArray(response.keywords)) {
        setKeywords(response.keywords);
      } else {
        setKeywords([]);
      }
      setRulesets([]);
      setIsSearchComplete(true);
      
      setTimeout(() => {
        setCurrentStep(1);
        setIsTransitioning(false);
      }, 800);
    } catch (error: any) {
      console.error('Error sending intent signal:', error);
      handleClose();
      
      const CustomToast = () => (
        <div className="flex items-start gap-4">
          <div className="w-10 h-10 flex-shrink-0 rounded-full bg-[#FEE2E2] flex items-center justify-center">
            <svg 
              className="w-5 h-5 text-[#E02C16]" 
              fill="none" 
              viewBox="0 0 24 24" 
              stroke="currentColor"
            >
              <path 
                strokeLinecap="round" 
                strokeLinejoin="round" 
                strokeWidth={2} 
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" 
              />
            </svg>
          </div>
          <div className="flex-1">
            <h3 className="text-[16px] font-semibold text-[#222222] mb-1">
              {error.response?.data?.detail?.includes('402: Payment required') 
                ? 'Subscription Limit Reached'
                : 'Error Creating Intent Signal'
              }
            </h3>
            <p className="text-[14px] text-[#666666] leading-relaxed">
              {error.response?.data?.detail?.includes('402: Payment required')
                ? 'You have reached the maximum of 2 active intent signals for your free plan. Please upgrade to continue.'
                : 'An error occurred while creating the intent signal. Please try again.'
              }
            </p>
          </div>
        </div>
      );

      toast(<CustomToast />, {
        position: "top-center",
        autoClose: 5000,
        hideProgressBar: true,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        className: "bg-[#FFFFFF] shadow-lg border border-[#EEEEEE] p-4 rounded-xl max-w-[440px] w-full"
      });
    } finally {
      setIsTransitioning(false);
      setIsInitialLoading(false);
    }
  };

  const handleKeywordSearch = async (newKeywords: string[], isFinalSubmit: boolean = false) => {
    if (!signalId) return;

    try {
      if (isFinalSubmit) {
        setIsKeywordLoading(true);
        await updateIntentSignal.mutateAsync({
          id: signalId,
          keywords: newKeywords,
          status: 'active'
        });
        onClose();
      } else {
        await updateIntentSignal.mutateAsync({
          id: signalId,
          keywords: newKeywords
        });
      }
    } catch (error) {
      console.error('Error updating keywords:', error);
    } finally {
      setIsKeywordLoading(false);
    }
  };

  const handleRulesetUpdate = async (newRulesets: string[], isFinalSubmit: boolean = false) => {
    if (!signalId) return;

    try {
      if (isFinalSubmit) {
        setIsTransitioning(true);
        await updateIntentSignal.mutateAsync({
          id: signalId,
          rulesets: newRulesets,
          status: 'active'
        });
        onClose();
      } else {
        await updateIntentSignal.mutateAsync({
          id: signalId,
          rulesets: newRulesets
        });
      }
    } catch (error) {
      console.error('Error updating rulesets:', error);
    } finally {
      setIsTransitioning(false);
    }
  };

  const handleStepChange = async () => {
    setIsTransitioning(true);
    
    try {
      if (currentStep === 1) {  // When moving from keywords slide to ruleset slide
        await updateIntentSignal.mutateAsync({
          id: signalId!,
          keywords: keywords,
        });
      } else if (currentStep === 2) {  // Final step - submit rulesets and generate leads
        await updateIntentSignal.mutateAsync({
          id: signalId!,
          rulesets: rulesets,
          status: 'active'
        });
        
        toast(<CustomSuccessToast />, {
          position: "top-center",
          autoClose: 4000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          className: "bg-[#FFFFFF] shadow-lg border border-[#EEEEEE] p-4 rounded-xl max-w-[440px] w-full",
          progressClassName: "bg-[#29DC5B]",
          transition: Slide,
        });
        
        onClose();
        return;
      }

      setTimeout(() => {
        setCurrentStep(prev => prev + 1);
        setIsTransitioning(false);
      }, 800);
    } catch (error) {
      console.error('Error updating intent signal:', error);
      setIsTransitioning(false);
    }
  };

  const handleAddRuleset = (ruleset: string) => {
    const newRulesets = [ruleset, ...rulesets];
    setRulesets(newRulesets);
  };

  const handleRemoveRuleset = (index: number) => {
    const newRulesets = rulesets.filter((_, i) => i !== index);
    setRulesets(newRulesets);
  };

  const renderBodyContent = () => {
    if (isTransitioning) {
      return <BufferingLoader />;
    }

    switch (currentStep) {
      case 0:
        return (
          <IntentSignalSlide
            inputValue={inputValue}
            onChange={setInputValue}
            onSearch={handleRunClick}
            onSuggestionSelect={handleSuggestionSelect}
            disabled={isSearchComplete}
          />
        );
      case 1:
        return (
          <KeywordSlide
            onAddKeyword={handleAddKeyword}
            onRemoveKeyword={handleRemoveKeyword}
            keywords={keywords}
          />
        );
      case 2:
        return (
          <RulesetSlide
            onAddRuleset={handleAddRuleset}
            onRemoveRuleset={handleRemoveRuleset}
            rulesets={rulesets}
          />
        );
      default:
        return null;
    }
  };

  const renderFooterButton = () => {
    return (
      <button 
        onClick={currentStep === 0 ? handleRunClick : handleStepChange}
        disabled={(currentStep === 0 && (!inputValue.trim() || isSearchComplete)) || 
                  (currentStep === 1 && keywords.length === 0) ||
                  isTransitioning}
        className={`p-2 rounded-lg flex items-center gap-2 ${
          ((currentStep === 0 && (!inputValue.trim() || isSearchComplete)) || 
           (currentStep === 1 && keywords.length === 0) ||
           isTransitioning)
            ? 'bg-[#DDDDDD] cursor-not-allowed' 
            : 'bg-[#29DC5B] hover:bg-[#0FAA3A]'
        }`}
      >
        <span className="text-[#222222] text-[14px] font-normal">
          {isTransitioning ? 'Processing...' : 
           currentStep === 2 ? 'Generate leads' : 'Next'}
        </span>
        <img src={runIcon} alt="run" className="w-4 h-4"/>
      </button>
    );
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    setIsDragging(true);
    setDragStart({
      x: e.clientX - position.x,
      y: e.clientY - position.y
    });
  };

  const handleMouseMove = (e: MouseEvent) => {
    if (isDragging && popupRef.current) {
      const newX = e.clientX - dragStart.x;
      const newY = e.clientY - dragStart.y;

      // Get window dimensions
      const windowWidth = window.innerWidth;
      const windowHeight = window.innerHeight;
      
      // Get popup dimensions
      const popupWidth = popupRef.current.offsetWidth;
      const popupHeight = popupRef.current.offsetHeight;

      // Calculate bounds
      const maxX = windowWidth - popupWidth;
      const maxY = windowHeight - popupHeight;

      // Constrain position within window bounds
      const boundedX = Math.min(Math.max(0, newX), maxX);
      const boundedY = Math.min(Math.max(0, newY), maxY);

      setPosition({
        x: boundedX,
        y: boundedY
      });
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  useEffect(() => {
    if (isDragging) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
    }

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, dragStart]);

  const resetPopupState = () => {
    setInputValue('');
    setSignalId(undefined);
    setCurrentStep(0);
    setKeywords([]);
    setRulesets([]);
    setIsSearchComplete(false);
    setIsTransitioning(false);
    setIsInitialLoading(false);
    setIsKeywordLoading(false);
  };

  // Add effect to reset state when popup closes
  useEffect(() => {
    if (!isOpen) {
      resetPopupState();
    }
  }, [isOpen]);

  const handleClose = () => {
    resetPopupState();
    onClose();
  };

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div 
      className={`fixed inset-0 flex items-center justify-center z-50 ${
        isSidebarOpen ? 'pl-[262px]' : 'pl-[72px]'
      }`}
      onClick={handleOverlayClick}
    >
      <div
        ref={popupRef}
        style={{
          transform: `translate(${position.x}px, ${position.y}px)`,
          transition: isDragging ? 'none' : 'transform 0.1s ease-out'
        }}
        className="w-[800px] h-[70%] bg-[#FFFFFF] rounded-[16px] shadow-2xl backdrop-blur-2xl flex flex-col"
        onClick={e => e.stopPropagation()}
      >
        {/* Header Section - Make it draggable */}
        <div
          onMouseDown={handleMouseDown}
          className="flex justify-between items-center px-4 py-[10px] border-b border-[#EEEEEE] cursor-move select-none"
        >
          <div className="flex items-center justify-center gap-1">
            <img src={createIcon} alt="create" className="w-[16px] h-[16px] bg-[#266DF0] rounded-[4px] stroke-[#FFFFFF]" />
            <h2 className="text-[12px]  font-normal text-[#222222]">
              Create intent signal
            </h2>
          </div>
          <button 
            onClick={handleClose}
            className="flex items-center justify-center text-[#75777C] w-5 h-5"
          >
            <img src={crossIcon} alt="cross" className="w-[10px] h-[10px] stroke-[#75777C]" />
          </button>
        </div>

        <ProgressBar currentStep={currentStep} />

        {/* Body Section */}
        <div className="flex-1 px-5 py-4 overflow-y-auto">
          {renderBodyContent()}
        </div>

        {/* Footer Section */}
        <div className="px-6 py-4 border-t border-[#EEEEEE] flex justify-end">
          {renderFooterButton()}
        </div>
      </div>
    </div>
  );
};

export default CreateIntentPopup; 